import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import moment from 'moment';

import { arrayMove } from 'react-sortable-hoc';

import { Formik } from 'formik';
import SkillsDialog from '../../DialogsCV/SkillsDialog';
import LanguageDialog from '../../DialogsCV/LanguageDialog';
import EducationDialog from '../../DialogsCV/EducationDialog';
import CertificateDialog from '../../DialogsCV/CertificateDialog';
import ProjectDialog from '../../DialogsCV/ProjectDialog';
import ConfirmDialog from 'components/Shared/ConfirmDialog';
import ConfirmDeleteDialog from 'components/Shared/ConfirmDeleteDialog';
import PrimaryButton from 'components/Shared/Buttons/PrimaryButton';
import SecondaryButton from 'components/Shared/Buttons/SecondaryButton';
import PaperElement from 'components/CandidateProfileJourney/SharedCandidateJourneyComponents/PaperElement';
import PaperHeader from 'components/CandidateProfileJourney/SharedCandidateJourneyComponents/PaperElement/PaperHeader';
import PaperContent from 'components/CandidateProfileJourney/SharedCandidateJourneyComponents/PaperElement/PaperContent';
import SortableList from 'components/CandidateProfileJourney/SharedCandidateJourneyComponents/SortableList';
import SortableSkillList from 'components/CandidateProfileJourney/SharedCandidateJourneyComponents/SortableSkillList';
import ImproveGeneralCvFormView from './ImproveGeneralCvFormView';

import { SELF_ASSESSMENT_MENU_ITEMS_KEYS } from 'components/TechnicalSelfAssessment/TechnicalSelfAssessmentConstants';
import { ROLES } from 'constants/userConstants';

import { ReactComponent as IconMaterialAdd } from "assets/oval-btn-plus.svg";

import './ImproveCv.scss';

const ImproveCv = ({
    sectionRefName,
    agencyId,
    userId,
    handleNextStep,
    discardAction,
    updateCandidateJourneyCvPage,
    employeeCvInformation,
    candidatePreferences,
    isCandidateMode,
    isEmployee,
    cvUserRole
}) => {
    const intl = useIntl();
    const formikRef = useRef();
    const history = useHistory();
    const prevLocation = useLocation().pathname;

    const skillsTypesDialogsOpenInitialState = {
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.PREFERRED_STACK]: false,
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.PROGRAMMING_LANGUAGES]: false,
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.FRAMEWORKS_AND_LIBRARIES]: false,
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.DATABASES]: false,
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.CLOUDS]: false,
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.TOOLS]: false,
        [SELF_ASSESSMENT_MENU_ITEMS_KEYS.OTHER]: false,
    };

    const [isConfirmRemoveDialogOpen, setIsConfirmRemoveDialogOpen] = useState(false);
    const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
    const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
    const [isSkillsDialogOpen, setIsSkillsDialogOpen] = useState(false);
    const [skillsDialogsOpen, setSkillsDialogsOpen] = useState(skillsTypesDialogsOpenInitialState);
    const [isCertificateDialogOpen, setIsCertificateDialogOpen] = useState(false);
    const [isEducationDialogOpen, setIsEducationDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState({});
    const [itemToRemove, setItemToRemove] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isConfirmationLeaveDialogOpen, setIsConfirmationLeaveDialogOpen] = useState(false);
    const [locationToGo, setLocationToGo] = useState(null);
    const [goToLocation, setGoToLocation] = useState(false);

    const editCvStartRef = useRef(null);
    const experienceRef = useRef(null);
    const profileSummaryRef = useRef(null);
    const preferredStackRef = useRef(null);
    const programmingLanguagesRef = useRef(null);
    const frameworksAndLibrariesRef = useRef(null);
    const databasesRef = useRef(null);
    const cloudsRef = useRef(null);
    const toolsRef = useRef(null);
    const otherRef = useRef(null);
    const educationRef = useRef(null);
    const certificatesRef = useRef(null);
    const languagesRef = useRef(null);
    const skillsRef = useRef(null);

    const refs = {
        profileSummary: profileSummaryRef,
        preferredStack: preferredStackRef,
        programmingLanguages: programmingLanguagesRef,
        frameworksAndLibraries: frameworksAndLibrariesRef,
        databases: databasesRef,
        commercialClouds: cloudsRef,
        tools: toolsRef,
        other: otherRef,
        education: educationRef,
        certificates: certificatesRef,
        languages: languagesRef,
        skills: skillsRef,
        experience: experienceRef,
        edit: editCvStartRef,
    }

    useEffect(() => {
        if (refs[sectionRefName].current) {
            refs[sectionRefName].current.scrollIntoView({ behavior: 'smooth' });
            refs[sectionRefName].current = '';
        }
    }, [refs]);

    useEffect(() => {
        if (goToLocation) {
            history.push(locationToGo);
        }
    }, [goToLocation])

    useEffect(() => {
        let unlisten = () => null;

        if (hasUnsavedChanges && !locationToGo) {
            unlisten = history.listen((location) => {
                if (location.pathname !== prevLocation) {
                    history.push(prevLocation);
                    setLocationToGo(location.pathname);
                    setIsConfirmationLeaveDialogOpen(true);
                }
            });
        }

        return () => {
            unlisten()
        }
    }, [hasUnsavedChanges]);

    const onSortEnd = (setterFunction, collection, type, { oldIndex, newIndex }) => {
        setterFunction(type, arrayMove(collection, oldIndex, newIndex));
    };

    const handleDialogToggle = (setIsDialogOpen, selectedItem) => {
        if (selectedItem) {
            setSelectedItem(selectedItem);
        } else {
            setSelectedItem({});
        }

        setIsDialogOpen(prevState => !prevState);
    };

    const toggleSkillsDialogsOpen = (dialogKey) => {
        setSkillsDialogsOpen(prevState => ({...prevState, [dialogKey]: !prevState[dialogKey]}));
    };

    const handleSubmitDialog = (field, newValue, itemIndex) => {
        const setFieldValue = formikRef.current.setFieldValue;
        const values = formikRef.current.values;

        setHasUnsavedChanges(true);

        //Edit item
        if (itemIndex || itemIndex === 0) {
            const { index, ...rest } = newValue;

            setFieldValue(field, [
                ...values[field].slice(0, index),
                rest,
                ...values[field].slice(index + 1)
            ]);

            return;
        }

        //Add item
        if (Array.isArray(newValue)) {
            setFieldValue(field, [...values[field], ...newValue]);
        } else {
            setFieldValue(field, [...values[field], newValue]);
        }
    };

    const handleRemoveItem = (field, itemToRemove) => {
        setItemToRemove({ field, ...itemToRemove });
        setIsConfirmRemoveDialogOpen(true);
    };

    const handleConfirmRemoveItem = () => {
        const setFieldValue = formikRef.current.setFieldValue;
        const values = formikRef.current.values;
        const { field, index } = itemToRemove;

        setFieldValue(field, [
            ...values[field].slice(0, index),
            ...values[field].slice(index + 1)
        ]);

        setIsConfirmRemoveDialogOpen(false);
        setHasUnsavedChanges(true);
    };

    const handleCloseConfirmDialog = () => {
        setIsConfirmRemoveDialogOpen(false);
        setItemToRemove({});
    };

    const handleCloseConfirmationLeaveDialog = () => setIsConfirmationLeaveDialogOpen(false);

    const handleValidationErrors = () => {
        formikRef.current.validateForm().then(errors => {
            if (Object.values(errors).length > 0) {
                profileSummaryRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        })
    };

    const handleConfirmLeavePage = () => {
        setIsConfirmationLeaveDialogOpen(false);
        setHasUnsavedChanges(false);
        setGoToLocation(true);
    };

    const handleRemoveSkillItem = (x, items, fieldValue, setFieldValue) => {
        const copy = [...items];
        copy.splice(x, 1);
        setFieldValue(fieldValue, copy);
        setHasUnsavedChanges(true);
    };

    const handleRateSkillItem = (itemIndex, itemRating, items, fieldValue, setFieldValue) => {
        const updatedItems = [...items];

        updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            rating: itemRating,
        };

        setFieldValue(fieldValue, updatedItems);
        setHasUnsavedChanges(true);
    };

    const initialGeneralInfo = isEmployee ? {
        position: employeeCvInformation.profileInformation.position || '',
        profileSummary: employeeCvInformation.profileSummary || '',
        experienceSince: employeeCvInformation.profileInformation.experienceSince ? moment.utc(employeeCvInformation.profileInformation.experienceSince) : null,
        profileType: employeeCvInformation.profileInformation.profileType || '',
    } : {
        position: employeeCvInformation.profileInformation.position || '',
        profileSummary: employeeCvInformation.profileSummary || '',
        experienceSince: employeeCvInformation.profileInformation.experienceSince ? moment.utc(employeeCvInformation.profileInformation.experienceSince) : null,
    };

    return (
        <div ref={editCvStartRef} className="edit-cv-wrapper">
            <PaperElement classes={["edit-container", "step-wrapper"]} >
                {isCandidateMode &&
                    <PaperElement>
                        <PaperHeader title={intl.formatMessage({ id: "improve-cv-title-text" })} />
                        <PaperContent classes={["content-width", "paper-element-description"]}>
                            <p>{intl.formatMessage({ id: "improve-cv-content-text" })}</p>
                        </PaperContent>
                    </PaperElement>
                }
                <Formik
                    innerRef={formikRef}
                    initialValues={{
                        generalInfo: initialGeneralInfo,
                        languages: employeeCvInformation.languages || [],
                        skills: employeeCvInformation.skills || [],
                        technologies: candidatePreferences.technologies || [],
                        education: employeeCvInformation.education || [],
                        certificates: employeeCvInformation.certificates || [],
                        projects: employeeCvInformation.projects || []
                    }}
                    enableReinitialize={true}
                    onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(false);
                        values.skills = values.skills.map(x => {
                            if (x.isNew) {
                                return { ...x };
                            }

                            return { _id: x._id, name: x.name, rating: x.rating };
                        });
                        if (!handleNextStep) {
                            updateCandidateJourneyCvPage(agencyId, userId, values);
                            discardAction();
                        } else {
                            const action = updateCandidateJourneyCvPage.bind(null, agencyId, userId, values);
                            handleNextStep(null, true, action);
                        }
                    }}
                >
                    {(props) => (
                        <>
                            <div ref={profileSummaryRef}>
                                <PaperElement>
                                    <PaperHeader title={intl.formatMessage({ id: "general" })} />
                                    <PaperContent>
                                        <ImproveGeneralCvFormView setHasUnsavedChanges={setHasUnsavedChanges} isEmployee={isEmployee} {...props} />
                                    </PaperContent>
                                </PaperElement>
                            </div>
                            {cvUserRole === ROLES.CANDIDATE ?
                                <Fragment>
                                    <div ref={preferredStackRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "preferred-stack-text" })} classes={["flex-header"]}>
                                                <IconMaterialAdd className="add-icon" onClick={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.PREFERRED_STACK)} />
                                            </PaperHeader>
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.technologies}
                                                    skillList={props.values.technologies}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.technologies, 'technologies', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.technologies, 'technologies', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.PREFERRED_STACK}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.PREFERRED_STACK]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.PREFERRED_STACK)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'technologies')}
                                                    skills={props.values.technologies}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                    <div ref={programmingLanguagesRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "programming-languages-text" })} classes={["flex-header"]}>
                                                <IconMaterialAdd className="add-icon" onClick={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.PROGRAMMING_LANGUAGES)} />
                                            </PaperHeader>
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.skills.filter(x => x.category === SELF_ASSESSMENT_MENU_ITEMS_KEYS.PROGRAMMING_LANGUAGES)}
                                                    skillList={props.values.skills}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.skills, 'skills', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.PROGRAMMING_LANGUAGES}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.PROGRAMMING_LANGUAGES]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.PROGRAMMING_LANGUAGES)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                    skills={props.values.skills}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                    <div ref={frameworksAndLibrariesRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "frameworks-and-libraries-text" })} classes={["flex-header"]}>
                                                <IconMaterialAdd className="add-icon" onClick={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.FRAMEWORKS_AND_LIBRARIES)} />
                                            </PaperHeader>
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.skills.filter(x => x.category === SELF_ASSESSMENT_MENU_ITEMS_KEYS.FRAMEWORKS_AND_LIBRARIES)}
                                                    skillList={props.values.skills}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.skills, 'skills', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.FRAMEWORKS_AND_LIBRARIES}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.FRAMEWORKS_AND_LIBRARIES]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.FRAMEWORKS_AND_LIBRARIES)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                    skills={props.values.skills}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                    <div ref={databasesRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "databases-text" })} classes={["flex-header"]}>
                                                <IconMaterialAdd className="add-icon" onClick={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.DATABASES)} />
                                            </PaperHeader>
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.skills.filter(x => x.category === SELF_ASSESSMENT_MENU_ITEMS_KEYS.DATABASES)}
                                                    skillList={props.values.skills}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.skills, 'skills', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.DATABASES}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.DATABASES]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.DATABASES)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                    skills={props.values.skills}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                    <div ref={cloudsRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "clouds-text" })} classes={["flex-header"]}>
                                                <IconMaterialAdd className="add-icon" onClick={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.CLOUDS)} />
                                            </PaperHeader>
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.skills.filter(x => x.category === SELF_ASSESSMENT_MENU_ITEMS_KEYS.CLOUDS)}
                                                    skillList={props.values.skills}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.skills, 'skills', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.CLOUDS}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.CLOUDS]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.CLOUDS)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                    skills={props.values.skills}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                    <div ref={toolsRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "tools-text" })} classes={["flex-header"]}>
                                                <IconMaterialAdd className="add-icon" onClick={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.TOOLS)} />
                                            </PaperHeader>
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.skills.filter(x => x.category === SELF_ASSESSMENT_MENU_ITEMS_KEYS.TOOLS)}
                                                    skillList={props.values.skills}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.skills, 'skills', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.TOOLS}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.TOOLS]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.TOOLS)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                    skills={props.values.skills}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                    <div ref={otherRef}>
                                        <PaperElement>
                                            <PaperHeader title={intl.formatMessage({ id: "other-text" })} classes={["flex-header"]} />
                                            <PaperContent classes={["paper-element-content"]}>
                                                <SortableSkillList
                                                    items={props.values.skills.filter(x => x.category === SELF_ASSESSMENT_MENU_ITEMS_KEYS.OTHER || !x.category)}
                                                    skillList={props.values.skills}
                                                    onRate={(itemIndex, itemRating) => handleRateSkillItem(itemIndex, itemRating, props.values.skills, 'skills', props.setFieldValue)}
                                                    onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                />
                                                <SkillsDialog
                                                    skillsCategory={SELF_ASSESSMENT_MENU_ITEMS_KEYS.OTHER}
                                                    open={skillsDialogsOpen[SELF_ASSESSMENT_MENU_ITEMS_KEYS.OTHER]}
                                                    onClose={() => toggleSkillsDialogsOpen(SELF_ASSESSMENT_MENU_ITEMS_KEYS.OTHER)}
                                                    onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                    skills={props.values.skills}
                                                />
                                            </PaperContent>
                                        </PaperElement>
                                    </div>
                                </Fragment>
                                :
                                <div ref={skillsRef}>
                                    <PaperElement>
                                        <PaperHeader title={intl.formatMessage({ id: "skills-text" })} classes={["flex-header"]}>
                                            <IconMaterialAdd className="add-icon" onClick={() => handleDialogToggle(setIsSkillsDialogOpen)} />
                                        </PaperHeader>
                                        <PaperContent classes={["paper-element-content"]}>
                                            <SortableSkillList
                                                items={props.values.skills}
                                                skillList={props.values.skills}
                                                onRemove={(x) => handleRemoveSkillItem(x, props.values.skills, 'skills', props.setFieldValue)}
                                                hideRating
                                            />
                                            <SkillsDialog
                                                open={isSkillsDialogOpen}
                                                onClose={handleDialogToggle.bind(null, setIsSkillsDialogOpen)}
                                                onSubmit={handleSubmitDialog.bind(null, 'skills')}
                                                skills={props.values.skills}
                                            />
                                        </PaperContent>
                                    </PaperElement>
                                </div>
                            }
                            <div ref={educationRef}>
                                <PaperElement>
                                    <PaperHeader title={intl.formatMessage({ id: "education" })} classes={["flex-header"]}>
                                        <IconMaterialAdd className="add-icon" onClick={() => handleDialogToggle(setIsEducationDialogOpen)} />
                                    </PaperHeader>
                                    <PaperContent classes={["paper-element-content"]}>
                                        <SortableList
                                            distance={1}
                                            items={props.values.education}
                                            onRemove={handleRemoveItem.bind(null, 'education')}
                                            onEdit={handleDialogToggle.bind(null, setIsEducationDialogOpen)}
                                            onSortEnd={(data) => onSortEnd(props.setFieldValue, props.values.education, 'education', data)}
                                        />
                                        <EducationDialog
                                            open={isEducationDialogOpen}
                                            onClose={handleDialogToggle.bind(null, setIsEducationDialogOpen)}
                                            onSubmit={handleSubmitDialog.bind(null, 'education')}
                                            selectedItem={selectedItem}
                                        />
                                    </PaperContent>
                                </PaperElement>
                            </div>
                            <div ref={experienceRef}>
                                <PaperElement>
                                    <PaperHeader title={intl.formatMessage({ id: "experience" })} classes={["flex-header"]}>
                                        <IconMaterialAdd className="add-icon" onClick={() => handleDialogToggle(setIsProjectDialogOpen)} />
                                    </PaperHeader>
                                    <PaperContent classes={["paper-element-content"]}>
                                        <SortableList
                                            distance={1}
                                            items={props.values.projects}
                                            onRemove={handleRemoveItem.bind(null, 'projects')}
                                            onEdit={handleDialogToggle.bind(null, setIsProjectDialogOpen)}
                                            onSortEnd={(data) => onSortEnd(props.setFieldValue, props.values.projects, 'projects', data)}
                                        />
                                        <ProjectDialog
                                            open={isProjectDialogOpen}
                                            onClose={handleDialogToggle.bind(null, setIsProjectDialogOpen)}
                                            onSubmit={handleSubmitDialog.bind(null, 'projects')}
                                            selectedItem={selectedItem}
                                        />
                                    </PaperContent>
                                </PaperElement>
                            </div>
                            <div ref={certificatesRef}>
                                <PaperElement>
                                    <PaperHeader title={intl.formatMessage({ id: "certificates" })} classes={["flex-header"]}>
                                        <IconMaterialAdd className="add-icon" onClick={() => handleDialogToggle(setIsCertificateDialogOpen)} />
                                    </PaperHeader>
                                    <PaperContent classes={["paper-element-content"]}>
                                        <SortableList
                                            distance={1}
                                            items={props.values.certificates}
                                            onRemove={handleRemoveItem.bind(null, 'certificates')}
                                            onEdit={handleDialogToggle.bind(null, setIsCertificateDialogOpen)}
                                            onSortEnd={(data) => onSortEnd(props.setFieldValue, props.values.certificates, 'certificates', data)}
                                        />
                                        <CertificateDialog
                                            open={isCertificateDialogOpen}
                                            onClose={handleDialogToggle.bind(null, setIsCertificateDialogOpen)}
                                            onSubmit={handleSubmitDialog.bind(null, 'certificates')}
                                            selectedItem={selectedItem}
                                        />
                                    </PaperContent>
                                </PaperElement>
                            </div>
                            <div ref={languagesRef}>
                                <PaperElement>
                                    <PaperHeader title={intl.formatMessage({ id: "languages" })} classes={["flex-header"]}>
                                        <IconMaterialAdd className="add-icon" onClick={() => handleDialogToggle(setIsLanguageDialogOpen)} />
                                    </PaperHeader>
                                    <PaperContent classes={["paper-element-content"]}>
                                        <SortableList
                                            distance={1}
                                            items={props.values.languages}
                                            onRemove={handleRemoveItem.bind(null, 'languages')}
                                            onEdit={handleDialogToggle.bind(null, setIsLanguageDialogOpen)}
                                            onSortEnd={(data) => onSortEnd(props.setFieldValue, props.values.languages, 'languages', data)}
                                            withTranslations
                                        />
                                        <LanguageDialog
                                            open={isLanguageDialogOpen}
                                            onClose={handleDialogToggle.bind(null, setIsLanguageDialogOpen)}
                                            onSubmit={handleSubmitDialog.bind(null, 'languages')}
                                            selectedItem={selectedItem}
                                        />
                                    </PaperContent>
                                </PaperElement>
                            </div>
                            <div className="journey-buttons-wrapper">
                                <SecondaryButton className="white" text={intl.formatMessage({ id: "cancel" })} handleClick={discardAction} />
                                <PrimaryButton className="purple" text={intl.formatMessage({ id: "save" })} handleClick={() => { handleValidationErrors(); props.handleSubmit() }} />
                            </div>
                        </>
                    )}
                </Formik >
                <ConfirmDeleteDialog
                    itemToDelete={itemToRemove.title || itemToRemove.name || itemToRemove.institution || ''}
                    handleDeleteItem={() => handleConfirmRemoveItem()}
                    openDialog={isConfirmRemoveDialogOpen}
                    handleCloseDialog={handleCloseConfirmDialog}
                />
                <ConfirmDialog
                    openDialog={isConfirmationLeaveDialogOpen}
                    dialogTitle={intl.formatMessage({ id: "confirm" })}
                    dialogSubtitle={intl.formatMessage({ id: "leave-page-confirmation-text" })}
                    discardButtonName={intl.formatMessage({ id: "no" })}
                    confirmButtonName={intl.formatMessage({ id: "yes" })}
                    handleCloseDialog={handleCloseConfirmationLeaveDialog}
                    handleConfirmDialog={handleConfirmLeavePage}
                />
            </PaperElement >
        </div>
    );
};


export default ImproveCv;
